<template>
    <v-container fluid class="td-root">
        <v-row>
            <v-col cols="12">
                <v-card class="td-surface td-header" outlined>
                    <v-card-title class="td-title">
                        <div>
                            <div class="headline font-weight-bold td-heading">{{ header.title }}</div>
                            <div class="caption td-muted">{{ header.subtitle }}</div>
                        </div>
                        <v-spacer />
                        <v-select v-model="selectedRange" :items="filterRanges" item-text="label" item-value="key" dense
                            outlined hide-details class="td-range" prepend-inner-icon="mdi-calendar"
                            @change="$emit('range-change', selectedRange)" />
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>

        <!-- KPIs principales -->
        <v-row>
            <v-col v-for="c in kpiCards" :key="c.key" cols="12" md="3">
                <v-card class="td-surface" outlined>
                    <v-card-text class="d-flex align-start">
                        <div class="flex-grow-1">
                            <div class="overline td-muted">{{ c.title }}</div>
                            <div class="text-h4 font-weight-bold">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="text-h4 font-weight-bold d-flex align-center" v-bind="attrs"
                                            v-on="on" :class="statusClass(c.tone)">
                                            <v-icon x-small class="mr-2" :class="statusClass(c.tone)">
                                                {{ statusIcon(c.tone) }}
                                            </v-icon>
                                            {{ c.value }}
                                        </div>
                                    </template>
                                    <span class="tooltip-text">{{ c.tooltip }}</span>
                                </v-tooltip>
                            </div>
                            <div v-if="c.sub" class="caption td-muted mt-1">{{ c.sub }}</div>
                        </div>

                        <v-avatar size="44" class="td-icon-bg">
                            <v-icon :color="iconColor(c.tone)">{{ c.icon }}</v-icon>
                        </v-avatar>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- KPIs secundarios -->
        <v-row>
            <v-col v-for="c in miniCards" :key="c.key" cols="12" sm="6" md="3">
                <v-card class="td-surface" outlined>
                    <v-card-text class="d-flex align-center">
                        <v-avatar size="40" class="td-icon-bg mr-3">
                            <v-icon :color="iconColor(c.tone)">{{ c.icon }}</v-icon>
                        </v-avatar>

                        <div class="flex-grow-1">
                            <div class="overline td-muted">{{ c.title }}</div>
                            <div class="text-h5 font-weight-bold" :class="toneClass(c.tone)">
                                {{ c.value }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Edge & Risk (Global + Rolling) -->
        <v-row>
            <v-col cols="12">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">
                        Edge & Risk
                    </v-card-title>

                    <v-card-text>
                        <v-row dense>
                            <!-- Global -->
                            <v-col cols="12" md="6">
                                <div class="td-block-title td-muted">Selected Range (Filtered)</div>

                                <div class="td-metric-grid">
                                    <!-- Expectancy (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Expectancy</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.expectancy.global', statsGlobal.expectancy).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.expectancy.global', statsGlobal.expectancy).class">
                                                        {{ metricStatus('edge.expectancy.global',
                                                            statsGlobal.expectancy).icon }}
                                                    </v-icon>
                                                    {{ fmtSignedPercent(calculateExpectancyPerTradePct()) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.expectancy.global',
                                                statsGlobal.expectancy).tooltip
                                                }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">per trade</div>
                                    </div>

                                    <!-- Avg Win (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Avg Win</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.avgWin', statsGlobal.avgWin).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.avgWin', statsGlobal.avgWin).class">
                                                        {{ metricStatus('edge.avgWin', statsGlobal.avgWin).icon }}
                                                    </v-icon>
                                                    {{ fmtPercent(calculateAvgWinPct()) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.avgWin', statsGlobal.avgWin).tooltip }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">closed winners</div>
                                    </div>

                                    <!-- Avg Loss (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Avg Loss</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).class">
                                                        {{ metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).icon
                                                        }}
                                                    </v-icon>
                                                    {{ fmtPercent(calculateAvgLossPct()) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).tooltip
                                                }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">closed losers</div>
                                    </div>

                                    <!-- Payoff Ratio (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Payoff Ratio</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.payoffRatio', statsGlobal.payoffRatio).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.payoffRatio', statsGlobal.payoffRatio).class">
                                                        {{ metricStatus('edge.payoffRatio',
                                                            statsGlobal.payoffRatio).icon }}
                                                    </v-icon>
                                                    {{ fmtNumber(calculatePayoffRatio(), 2) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.payoffRatio', statsGlobal.payoffRatio).tooltip
                                                }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">AvgWin / AvgLoss</div>
                                    </div>

                                    <!-- Rolling Drawdown -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Rolling Drawdown</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('risk.rollingDD', statsGlobal.rollingDDAbs).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('risk.rollingDD', statsGlobal.rollingDDAbs).class">
                                                        {{ metricStatus('risk.rollingDD',
                                                            statsGlobal.maxDrawdownAbs).icon }}
                                                    </v-icon>
                                                    {{ fmtPercent(statsGlobal.rollingDD) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('risk.rollingDD', statsGlobal.rollingDDAbs).tooltip
                                            }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">
                                            max DD -{{ fmtPercent(kpis.maxDrawdownAbs) }}
                                        </div>
                                    </div>

                                    <!-- Time in Drawdown (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Time in Drawdown</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('risk.timeInDD', timeInDrawdown.totalDays).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('risk.timeInDD', timeInDrawdown.totalDays).class">
                                                        {{ metricStatus('risk.timeInDD', timeInDrawdown.totalDays).icon
                                                        }}
                                                    </v-icon>
                                                    {{ timeInDrawdown.totalDays }} days
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('risk.timeInDD', timeInDrawdown.totalDays).tooltip
                                                }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">
                                            max streak {{ timeInDrawdown.maxStreakDays }} days
                                        </div>
                                    </div>
                                </div>

                            </v-col>

                            <!-- Rolling -->
                            <v-col cols="12" md="6">
                                <div class="td-block-title td-muted">Rolling Snapshot (Last {{ rollingWindow }} Trades ·
                                    Unfiltered)</div>

                                <div class="td-metric-grid">
                                    <!-- Expectancy (Rolling) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Expectancy</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.expectancy.rolling', statsRolling.expectancy, { consecutiveNeg: rollingExpectancyNegStreak }).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.expectancy.rolling', statsRolling.expectancy, { consecutiveNeg: rollingExpectancyNegStreak }).class">
                                                        {{ metricStatus('edge.expectancy.rolling',
                                                            statsRolling.expectancy, {
                                                            consecutiveNeg:
                                                                rollingExpectancyNegStreak
                                                        }).icon }}
                                                    </v-icon>
                                                    {{ fmtSignedPercent(calculateExpectancyPerTradePct(true, true)) }}
                                                </div>
                                            </template>

                                            <span>
                                                {{ metricStatus('edge.expectancy.rolling', statsRolling.expectancy, {
                                                    consecutiveNeg: rollingExpectancyNegStreak
                                                }).tooltip }}
                                            </span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">per trade</div>
                                    </div>

                                    <!-- Avg Win (Rolling) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Avg Win</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.avgWin', statsRolling.avgWin).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.avgWin', statsRolling.avgWin).class">
                                                        {{ metricStatus('edge.avgWin', statsRolling.avgWin).icon }}
                                                    </v-icon>
                                                    {{ fmtPercent(calculateAvgWinPct(true, true)) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.avgWin', statsRolling.avgWin).tooltip }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">closed winners</div>
                                    </div>

                                    <!-- Avg Loss (Rolling) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Avg Loss</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).class">
                                                        {{ metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).icon
                                                        }}
                                                    </v-icon>
                                                    {{ fmtPercent(calculateAvgLossPct(true, true)) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.avgLossAbs', statsGlobal.avgLossAbs).tooltip
                                            }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">closed losers</div>
                                    </div>

                                    <!-- Payoff Ratio (Rolling) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Payoff Ratio</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('edge.payoffRatio', statsRolling.payoffRatio).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('edge.payoffRatio', statsRolling.payoffRatio).class">
                                                        {{ metricStatus('edge.payoffRatio',
                                                            statsRolling.payoffRatio).icon }}
                                                    </v-icon>
                                                    {{ fmtNumber(calculatePayoffRatio(true, true), 2) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('edge.payoffRatio', statsRolling.payoffRatio).tooltip
                                            }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">AvgWin / AvgLoss</div>
                                    </div>

                                    <!-- Rolling Drawdown -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Rolling Drawdown</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('risk.rollingDD', statsRolling.rollingDDAbs).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('risk.rollingDD', statsRolling.rollingDDAbs).class">
                                                        {{ metricStatus('risk.rollingDD',
                                                            statsRolling.maxDrawdownAbs).icon }}
                                                    </v-icon>
                                                    {{ fmtPercent(statsRolling.rollingDD) }}
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('risk.rollingDD', statsRolling.rollingDDAbs).tooltip
                                            }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">
                                            max DD -{{ fmtPercent(kpis.maxDrawdownAbs) }}
                                        </div>
                                    </div>

                                    <!-- Time in Drawdown (Global) -->
                                    <div class="td-metric">
                                        <div class="td-metric-label td-muted">Time in Drawdown</div>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="td-metric-value d-flex align-center" v-bind="attrs"
                                                    v-on="on"
                                                    :class="metricStatus('risk.timeInDD', timeInDrawdownRolling.totalDays).class">
                                                    <v-icon x-small class="mr-2"
                                                        :class="metricStatus('risk.timeInDD', timeInDrawdownRolling.totalDays).class">
                                                        {{ metricStatus('risk.timeInDD',
                                                            timeInDrawdownRolling.totalDays).icon
                                                        }}
                                                    </v-icon>
                                                    {{ timeInDrawdownRolling.totalDays }} days
                                                </div>
                                            </template>

                                            <span>{{ metricStatus('risk.timeInDD',
                                                timeInDrawdownRolling.totalDays).tooltip
                                                }}</span>
                                        </v-tooltip>

                                        <div class="td-metric-sub td-muted">
                                            max streak {{ timeInDrawdownRolling.maxStreakDays }} days
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>


        <!-- Charts: Equity + Drawdown -->
        <v-row>
            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Equity Performance (%)</v-card-title>
                    <v-card-text>
                        <apexchart type="area" height="300" :options="equityChartOptions" :series="equitySeries" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">
                        <span>Drawdown</span>
                        <v-spacer />
                        <span class="caption td-muted">
                            Maximum:
                            <span class="font-weight-medium td-bad">{{ fmtPercent(drawdownChartData.maxDrawdownAbs)
                                }}</span>
                        </span>
                    </v-card-title>
                    <v-card-text>
                        <apexchart type="area" height="300" :options="drawdownChartOptions" :series="drawdownSeries" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Charts: Motivos + Distribución -->
        <v-row>
            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Exit Performance Breakdown</v-card-title>

                    <v-card-text>
                        <v-row dense>
                            <!-- Donut -->
                            <v-col cols="12" md="7">
                                <apexchart type="donut" height="252" :options="closeReasonsChartOptions"
                                    :series="closeReasonsSeries" />
                            </v-col>

                            <!-- P&L (Return) por tipo de salida -->
                            <v-col cols="12" md="5" class="d-flex">
                                <div class="td-exit-panel">
                                    <div v-for="r in exitReasonPnLRows" :key="r.key" class="td-exit-row">
                                        <div class="td-exit-row-head">
                                            <div class="td-exit-label">
                                                <span class="td-dot" :style="{ backgroundColor: r.color }"></span>
                                                <span>{{ r.label }}</span>
                                            </div>

                                            <!-- P&L en % -->
                                            <div class="td-exit-value" :class="r.pnlPct >= 0 ? 'td-good' : 'td-bad'">
                                                P&L {{ fmtPercent(r.pnlPct) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>


            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="pb-0 td-card-title td-heading">Trade Allocation</v-card-title>
                    <v-card-text class="pt-0">
                        <v-row dense class="pt-0">

                            <!-- LONG -->
                            <v-col cols="12" sm="6">
                                <v-card outlined class="td-subcard td-subcard-good">
                                    <v-card-title class="subtitle-2 font-weight-bold td-heading pb-0">
                                        <v-icon left small color="green darken-1">mdi-arrow-top-right</v-icon>
                                        Long
                                    </v-card-title>

                                    <v-card-text class="pb-2">
                                        <div class="td-kv">
                                            <span>Trades</span>
                                            <b class="td-heading">{{ byType.long.trades }}</b>
                                        </div>

                                        <!-- Win Rate -->
                                        <div class="td-kv">
                                            <span>Win Rate</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.long.winRate', byType.long.winRate).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.long.winRate', byType.long.winRate).class">
                                                            {{ metricStatus('alloc.long.winRate',
                                                                byType.long.winRate).icon }}
                                                        </v-icon>
                                                        {{ fmtPercent(byType.long.winRate) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.long.winRate', byType.long.winRate).tooltip
                                                    }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Avg Trade -->
                                        <div class="td-kv">
                                            <span>Avg Trade</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.long.avgTradePct', byType.long.avgTradePct).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.long.avgTradePct', byType.long.avgTradePct).class">
                                                            {{ metricStatus('alloc.long.avgTradePct',
                                                                byType.long.avgTradePct).icon }}
                                                        </v-icon>
                                                        {{ fmtSignedPercent(byType.long.avgTradePct) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.long.avgTradePct',
                                                    byType.long.avgTradePct).tooltip
                                                    }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Profit Factor -->
                                        <div class="td-kv">
                                            <span>Profit Factor</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.long.profitFactor', byType.long.profitFactor).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.long.profitFactor', byType.long.profitFactor).class">
                                                            {{ metricStatus('alloc.long.profitFactor',
                                                                byType.long.profitFactor).icon }}
                                                        </v-icon>
                                                        {{ fmtNumber(byType.long.profitFactor, 2) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.long.profitFactor',
                                                    byType.long.profitFactor).tooltip }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Total P&L (%) -->
                                        <div class="td-kv">
                                            <span>Total P&L (%)</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.long.pnlTotal', byType.long.pnlPct).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.long.pnlTotal', byType.long.pnlPct).class">
                                                            {{ metricStatus('alloc.long.pnlTotal',
                                                                byType.long.pnlPct).icon }}
                                                        </v-icon>
                                                        {{ fmtPercent(byType.long.pnlPct) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.long.pnlTotal', byType.long.pnlPct).tooltip
                                                    }}</span>
                                            </v-tooltip>
                                        </div>

                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- SHORT -->
                            <v-col cols="12" sm="6">
                                <v-card outlined class="td-subcard td-subcard-bad">
                                    <v-card-title class="subtitle-2 font-weight-bold td-headin pb-0">
                                        <v-icon left small color="red darken-1">mdi-arrow-bottom-right</v-icon>
                                        Short
                                    </v-card-title>

                                    <v-card-text class="py-2">
                                        <div class="td-kv">
                                            <span>Trades</span>
                                            <b class="td-heading">{{ byType.short.trades }}</b>
                                        </div>

                                        <!-- Win Rate -->
                                        <div class="td-kv">
                                            <span>Win Rate</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.short.winRate', byType.short.winRate).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.short.winRate', byType.short.winRate).class">
                                                            {{ metricStatus('alloc.short.winRate',
                                                                byType.short.winRate).icon }}
                                                        </v-icon>
                                                        {{ fmtPercent(byType.short.winRate) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.short.winRate',
                                                    byType.short.winRate).tooltip
                                                    }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Avg Trade -->
                                        <div class="td-kv">
                                            <span>Avg Trade</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.short.avgTradePct', byType.short.avgTradePct).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.short.avgTradePct', byType.short.avgTradePct).class">
                                                            {{ metricStatus('alloc.short.avgTradePct',
                                                                byType.short.avgTradePct).icon }}
                                                        </v-icon>
                                                        {{ fmtSignedPercent(byType.short.avgTradePct) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.short.avgTradePct',
                                                    byType.short.avgTradePct).tooltip }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Profit Factor -->
                                        <div class="td-kv">
                                            <span>Profit Factor</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.short.profitFactor', byType.short.profitFactor).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.short.profitFactor', byType.short.profitFactor).class">
                                                            {{ metricStatus('alloc.short.profitFactor',
                                                                byType.short.profitFactor).icon }}
                                                        </v-icon>
                                                        {{ fmtNumber(byType.short.profitFactor, 2) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.short.profitFactor',
                                                    byType.short.profitFactor).tooltip }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- Total P&L (%) -->
                                        <div class="td-kv">
                                            <span>Total P&L (%)</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <b v-bind="attrs" v-on="on" class="d-flex align-center"
                                                        :class="metricStatus('alloc.short.pnlTotal', byType.short.pnlPct).class">
                                                        <v-icon x-small class="mr-1"
                                                            :class="metricStatus('alloc.short.pnlTotal', byType.short.pnlPct).class">
                                                            {{ metricStatus('alloc.short.pnlTotal',
                                                                byType.short.pnlPct).icon }}
                                                        </v-icon>
                                                        {{ fmtPercent(byType.short.pnlPct) }}
                                                    </b>
                                                </template>
                                                <span>{{ metricStatus('alloc.short.pnlTotal',
                                                    byType.short.pnlPct).tooltip
                                                    }}</span>
                                            </v-tooltip>
                                        </div>

                                    </v-card-text>
                                </v-card>
                            </v-col>

                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

        </v-row>

        <!-- Trade Duration: Winners + Losers (with SMA10) -->
        <v-row>
            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Winner Trade Duration</v-card-title>
                    <v-card-text>
                        <apexchart type="area" height="300" :options="winnerDurationChartOptions"
                            :series="winnerDurationSeries" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Loser Trade Duration</v-card-title>
                    <v-card-text>
                        <apexchart type="area" height="300" :options="loserDurationChartOptions"
                            :series="loserDurationSeries" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Monthly trades data -->
        <v-row>
            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Monthly Closed-Trade Return (%)</v-card-title>
                    <v-card-text>
                        <apexchart type="line" height="300" :options="monthlyPnlChartOptions"
                            :series="monthlyPnlSeries" />
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" lg="6">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">Monthly Closed Trades</v-card-title>
                    <v-card-text>
                        <apexchart type="line" height="300" :options="monthlyClosedTradesChartOptions"
                            :series="monthlyClosedTradesSeries" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Tabla trades -->
        <v-row>
            <v-col cols="12">
                <v-card class="td-surface" outlined>
                    <v-card-title class="td-card-title td-heading">
                        {{ $t('dashboard.table.title') }}
                        <v-spacer />
                        <v-text-field v-model="tableSearch" dense outlined hide-details prepend-inner-icon="mdi-magnify"
                            label="Search" class="td-search" />
                    </v-card-title>

                    <v-data-table :headers="tableHeaders" :items="tradesListFiltered" :search="tableSearch" dense
                        class="td-table" :items-per-page="10" :mobile-breakpoint="0">
                        <template v-slot:item.mode="{ item }">
                            <div class="d-flex align-center" :class="item.mode === 'SPOT_LONG' ? 'td-good' : 'td-bad'">
                                <v-icon small class="mr-1"
                                    :color="item.mode === 'SPOT_LONG' ? 'green darken-1' : 'red darken-1'">
                                    {{ item.mode === 'SPOT_LONG' ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}
                                </v-icon>
                                <span class="text-capitalize font-weight-medium">{{ item.mode === 'SPOT_LONG' ?
                                    $t('dashboard.table.modeLong') : $t('dashboard.table.modeShort') }}</span>
                            </div>
                        </template>

                        <template v-slot:item.status="{ item }">
                            <div class="d-flex align-center td-muted">
                                <v-icon x-small class="mr-1"
                                    :color="item.status === 'CLOSED' ? 'grey darken-1' : 'green darken-1'">mdi-circle</v-icon>
                                <span>{{ item.status === 'CLOSED' ? $t('dashboard.table.closed') :
                                    $t('dashboard.table.open') }}</span>
                            </div>
                        </template>

                        <template v-slot:item.entry="{ item }">
                            <span class="td-muted">{{ fmtMoney(item.entryAvgPrice) }}</span>
                        </template>

                        <template v-slot:item.exit="{ item }">
                            <span class="td-muted">{{ item.exitAvgPrice != null ? fmtMoney(item.exitAvgPrice) : '--'
                                }}</span>
                        </template>

                        <template v-slot:item.closeReason="{ item }">
                            <v-chip v-if="item && item.closeReason !== null" small outlined
                                :color="closeReasonColor(item.closeReason)" class="font-weight-medium">
                                {{ closeReasonName(item.closeReason) }}
                            </v-chip>
                            <span v-else class="td-muted">--</span>
                        </template>

                        <template v-slot:item.pnl="{ item }">
                            <span v-if="item.realizedPnlQuote !== null" class="font-weight-bold"
                                :class="item.realizedPnlQuote >= 0 ? 'td-good' : 'td-bad'">
                                {{ fmtSignedMoney(calculateTradeResult(item.mode, item.entryAvgPrice, item.exitAvgPrice,
                                    item.qty, tradeFeesPct).profit) }}
                            </span>
                            <span v-else class="td-muted">--</span>
                        </template>

                        <template v-slot:item.pnlPct="{ item }">
                            <span v-if="item.realizedPnlQuote !== null" class="font-weight-medium"
                                :class="item.realizedPnlQuote >= 0 ? 'td-good' : 'td-bad'">
                                {{ fmtSignedPercent(calculateTradeResult(item.mode, item.entryAvgPrice,
                                    item.exitAvgPrice, item.qty, tradeFeesPct).profitPercentage) }}
                            </span>
                            <span v-else class="td-muted">--</span>
                        </template>

                        <template v-slot:item.date="{ item }">
                            <span class="td-muted">{{ formatDate(item.openedAt) }}</span>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import axios from 'axios';

export default {
    name: "TradingDashboardMaterial",
    components: {
        apexchart: VueApexCharts
    },

    data() {
        return {
            tradeFeesPct: 0.1,
            tradesList: [],
            header: { title: this.$t('dashboard.title'), subtitle: this.$t('dashboard.subtitle') },
            filterRanges: [
                { key: "all", label: this.$t('dashboard.filter.all') },
                { key: "1y", label: this.$t('dashboard.filter.lastYear') },
                { key: "cy", label: this.$t('dashboard.filter.currentYear') },
                { key: "6m", label: this.$t('dashboard.filter.sexMonths') },
                { key: "3m", label: this.$t('dashboard.filter.threeMonths') },
                { key: "30d", label: this.$t('dashboard.filter.thirtyDays') },
                { key: "7d", label: this.$t('dashboard.filter.sevenDays') }
            ],
            selectedRange: "all",
            loadingTrades: false,
            rollingWindow: 20,
            baselines: {
                expectedMaxDrawdownAbs: 0.1, // Max DD esperado “normal” (en fracción: 0.1 = 10%)
                timeInDDDaysHist: 14, // Time in drawdown histórico (días)
                rollingDrawdownAbsMean: 0.1, // Rolling drawdown “medio” (abs) (en fracción)
            },
            tableSearch: "",
            tableHeaders: [
                { text: this.$t('dashboard.table.header.side'), value: "mode" },
                { text: this.$t('dashboard.table.header.status'), value: "status" },
                { text: this.$t('dashboard.table.header.entryPrice'), value: "entry" },
                { text: this.$t('dashboard.table.header.exitPrice'), value: "exit" },
                { text: this.$t('dashboard.table.header.exitType'), value: "closeReason" },
                { text: this.$t('dashboard.table.header.pnl'), value: "pnl" },
                { text: this.$t('dashboard.table.header.pnlPct'), value: "pnlPct" },
                { text: this.$t('dashboard.table.header.date'), value: "date" }
            ],
        };
    },

    computed: {
        tradesListFiltered() {
            if (!Array.isArray(this.tradesList) || this.tradesList.length === 0 || this.selectedRange === 'all') {
                return this.tradesList || [];
            }

            const now = new Date();
            let startDate = null;

            switch (this.selectedRange) {
                case '1y':
                    startDate = new Date(now);
                    startDate.setFullYear(now.getFullYear() - 1);
                    break;

                case 'cy':
                    startDate = new Date(now.getFullYear(), 0, 1);
                    break;

                case '6m':
                    startDate = new Date(now);
                    startDate.setMonth(now.getMonth() - 6);
                    break;

                case '3m':
                    startDate = new Date(now);
                    startDate.setMonth(now.getMonth() - 3);
                    break;

                case '30d':
                    startDate = new Date(now);
                    startDate.setDate(now.getDate() - 30);
                    break;

                case '7d':
                    startDate = new Date(now);
                    startDate.setDate(now.getDate() - 7);
                    break;

                default:
                    return this.tradesList;
            }

            return this.tradesList.filter(t => {
                if (!t) return false;

                const dt = t.closedAt != null ? new Date(t.closedAt) :
                    t.openedAt != null ? new Date(t.openedAt) :
                        null;

                if (!dt || Number.isNaN(dt.getTime())) return false;

                return dt >= startDate && dt <= now;
            });
        },
        totalTradesClosed() {
            if (!Array.isArray(this.tradesListFiltered)) return 0;
            return this.tradesListFiltered.filter(t => t && t.closedAt != null).length;
        },
        bestTradePct() {
            if (!Array.isArray(this.tradesListFiltered)) return 0;

            let best = null;

            for (let i = 0; i < this.tradesListFiltered.length; i++) {
                const t = this.tradesListFiltered[i];
                if (!t) continue;

                // Ignorar abiertos
                if (t.closedAt == null) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);

                // Si quieres contemplar coste fijo máximo de margin en moneda quote:
                const r = result.profit / entryValue; // decimal

                if (!Number.isFinite(r)) continue;
                if (best === null || r > best) best = r;
            }

            return best === null ? 0 : best;
        },
        worstTradePct() {
            if (!Array.isArray(this.tradesListFiltered)) return 0;

            let worst = null;

            for (let i = 0; i < this.tradesListFiltered.length; i++) {
                const t = this.tradesListFiltered[i];
                if (!t) continue;

                // Ignorar abiertos
                if (t.closedAt == null) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);

                // coste fijo máximo de margin
                const r = result.profit / entryValue; // decimal

                if (!Number.isFinite(r)) continue;
                if (worst === null || r < worst) worst = r;
            }

            return worst === null ? 0 : worst;
        },
        winRatePct() {
            if (!Array.isArray(this.tradesListFiltered)) return 0;

            let wins = 0;
            let total = 0;

            for (let i = 0; i < this.tradesListFiltered.length; i++) {
                const t = this.tradesListFiltered[i];
                if (!t) continue;

                // Ignorar abiertos
                if (t.closedAt == null) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);

                // retorno neto (incluyendo coste fijo de margin si aplica)
                const r = result.profit / entryValue;

                if (!Number.isFinite(r)) continue;

                total++;
                if (r > 0) wins++;
            }

            return total === 0 ? 0 : (wins / total);
        },
        rollingExpectancyNegStreak() {
            const W = Number(this.rollingWindow) || 20;

            const closed = this._getClosedTrades({ unfiltered: true, onlyLastN: false });
            if (closed.length < W) return 0;

            const expectancyOfSlice = (slice) => {
                let wins = 0, losses = 0;
                let sumWin = 0, sumLoss = 0;

                for (let i = 0; i < slice.length; i++) {
                    const t = slice[i];

                    const entryPrice = Number(t.entryAvgPrice);
                    const exitPrice = Number(t.exitAvgPrice);
                    const qty = Number(t.qty);

                    const entryValue = entryPrice * qty;
                    if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                    const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                    const r = result.profit / entryValue;
                    if (!Number.isFinite(r) || r === 0) continue;

                    if (r > 0) { wins++; sumWin += r; }
                    else { losses++; sumLoss += r; }
                }

                const total = wins + losses;
                if (total === 0) return 0;

                const avgWin = wins ? (sumWin / wins) : 0;
                const avgLoss = losses ? (sumLoss / losses) : 0;

                const winRate = wins / total;
                const lossRate = losses / total;

                return (winRate * avgWin) + (lossRate * avgLoss);
            };

            let streak = 0;
            for (let start = 0; start + W <= closed.length; start++) {
                const exp = expectancyOfSlice(closed.slice(start, start + W));
                if (exp < 0) streak++;
                else break;
            }

            return streak;
        },
        structuralHealth() {
            const ms = (key, value, extra) => this.metricStatus(key, value, extra);

            const map = {
                "edge.expectancy.global": ms("edge.expectancy.global", this.statsGlobal.expectancy),

                "edge.expectancy.rolling": ms(
                    "edge.expectancy.rolling",
                    this.statsRolling.expectancy,
                    { consecutiveNeg: this.rollingExpectancyNegStreak }
                ),

                "kpi.profitFactor": ms("kpi.profitFactor", this.statsGlobal.profitFactor),
                "edge.payoffRatio": ms("edge.payoffRatio", this.statsGlobal.payoffRatio),

                "kpi.maxDrawdown": ms("kpi.maxDrawdown", this.kpis.maxDrawdownAbs),

                "risk.timeInDD": ms("risk.timeInDD", this.timeInDrawdown.totalDays),
                "risk.rollingDD": ms("risk.rollingDD", this.statsGlobal.rollingDDAbs),

                "kpi.pnlTotal": ms("kpi.pnlTotal", this.kpis.pnlTotal),

                "kpi.winRate": ms("kpi.winRate", this.kpis.winRate, { payoffRatio: this.statsGlobal.payoffRatio }),

                "edge.avgWin": ms("edge.avgWin", this.statsGlobal.avgWin),
                "edge.avgLossAbs": ms("edge.avgLossAbs", this.statsGlobal.avgLossAbs)
            };

            const toneCount = (keys, tone) =>
                keys.reduce((acc, k) => acc + (map[k]?.tone === tone ? 1 : 0), 0);

            const structuralRed = toneCount(this.structuralMetricKeys, "bad");
            const structuralAmber = toneCount(this.structuralMetricKeys, "warn");

            const regimeRed = toneCount(this.regimeMetricKeys, "bad");
            const regimeAmber = toneCount(this.regimeMetricKeys, "warn");

            const contextRed = toneCount(this.contextMetricKeys, "bad");
            const contextAmber = toneCount(this.contextMetricKeys, "warn");

            const possibleDegradation =
                structuralRed >= 2 ||
                (
                    map["edge.expectancy.rolling"]?.tone === "bad" &&
                    this.rollingExpectancyNegStreak >= 10 &&
                    structuralRed >= 1
                );

            const watch =
                !possibleDegradation &&
                (
                    structuralRed === 1 ||
                    (structuralAmber + regimeAmber) >= 2 ||
                    regimeRed >= 1
                );

            let tone = "good";
            let label = "Healthy";
            if (possibleDegradation) { tone = "bad"; label = "Possible degradation"; }
            else if (watch) { tone = "warn"; label = "On watch"; }

            const score =
                100
                - (structuralRed * 25 + structuralAmber * 10)
                - (regimeRed * 12 + regimeAmber * 6)
                - (contextRed * 6 + contextAmber * 3);

            const scoreClamped = Math.max(0, Math.min(100, Math.round(score)));

            const pick = (keys, tone) => keys.filter(k => map[k]?.tone === tone).map(k => k);

            const redList = [
                ...pick(this.structuralMetricKeys, "bad"),
                ...pick(this.regimeMetricKeys, "bad"),
                ...pick(this.contextMetricKeys, "bad")
            ];
            const amberList = [
                ...pick(this.structuralMetricKeys, "warn"),
                ...pick(this.regimeMetricKeys, "warn"),
                ...pick(this.contextMetricKeys, "warn")
            ];

            const niceName = (k) => ({
                "edge.expectancy.global": "Expectancy (Global)",
                "edge.expectancy.rolling": "Expectancy (Rolling)",
                "kpi.profitFactor": "Profit Factor",
                "edge.payoffRatio": "Payoff Ratio",
                "kpi.maxDrawdown": "Max Drawdown",
                "risk.timeInDD": "Time in Drawdown",
                "risk.rollingDD": "Rolling Drawdown",
                "kpi.pnlTotal": "Total P&L",
                "kpi.winRate": "Win Rate",
                "edge.avgWin": "Avg Win",
                "edge.avgLossAbs": "Avg Loss"
            }[k] || k);

            const tooltip = [
                `Status: ${label}`,
                structuralRed >= 2
                    ? "Trigger: ≥2 structural metrics in red."
                    : structuralRed === 1
                        ? "Note: 1 structural metric in red (watch)."
                        : "No structural reds detected.",
                redList.length ? `Red: ${redList.map(niceName).join(", ")}` : "Red: none",
                amberList.length ? `Amber: ${amberList.map(niceName).join(", ")}` : "Amber: none"
            ].join("\n");

            return {
                tone,
                label,
                score: scoreClamped,
                tooltip,
                breakdown: { structuralRed, structuralAmber, regimeRed, regimeAmber, contextRed, contextAmber }
            };
        },
        statsGlobal() {
            const avgLossPct = this.calculateAvgLossPct(false, false);
            const rollingDD = this.calculateRollingDrawdownPct(false, false);

            return {
                expectancy: this.calculateExpectancyPerTradePct(false, false),
                profitFactor: this.calculateProfitFactor(this.tradeFeesPct),
                payoffRatio: this.calculatePayoffRatio(false, false),
                avgWin: this.calculateAvgWinPct(false, false),
                avgLossPct,
                avgLossAbs: Math.abs(avgLossPct),
                rollingDD,
                rollingDDAbs: Math.abs(rollingDD)
            };
        },

        statsRolling() {
            // last N + UNFILTERED
            const avgLossPct = this.calculateAvgLossPct(true, true);
            const rollingDD = this.calculateRollingDrawdownPct(true, true);

            return {
                expectancy: this.calculateExpectancyPerTradePct(true, true),
                payoffRatio: this.calculatePayoffRatio(true, true),
                avgWin: this.calculateAvgWinPct(true, true),
                avgLossPct,
                avgLossAbs: Math.abs(avgLossPct),
                rollingDD,
                rollingDDAbs: Math.abs(rollingDD)
            };
        },

        timeInDrawdown() {
            return this.calculateTimeInDrawdownDays(false, false);
        },

        timeInDrawdownRolling() {
            return this.calculateTimeInDrawdownDays(true, true);
        },

        kpis() {
            return {
                profitFactor: this.calculateProfitFactor(this.tradeFeesPct),    // decimal
                pnlTotal: this.calculateTotalPnL(this.tradeFeesPct),            // decimal
                winRate: this.winRatePct,                                       // decimal 0..1
                maxDrawdownAbs: this.calculateMaxDrawdownPct(this.tradeFeesPct) // decimal
            };
        },
        equitySeries() {
            const d = this.equityChartData;
            return [
                { name: "Equity", type: "area", data: d.equity },
                { name: "SMA(10)", type: "line", data: d.sma }
            ];
        },
        equityChartOptions() {
            const d = this.equityChartData;

            return {
                // 1ª serie = Equity, 2ª serie = SMA
                colors: ["#2563eb", "#94a3b8"],

                chart: {
                    type: "area",
                    toolbar: { show: false },
                    foreColor: "#475569",
                    zoom: { enabled: false },
                    background: "transparent",
                    animations: { enabled: true, easing: "easeinout", speed: 650 }
                },

                stroke: {
                    curve: "smooth",
                    width: [3, 2],
                    colors: ["#2563eb", "#94a3b8"],
                    // SMA con línea discontinua (opcional, pero suele quedar como en dashboards)
                    dashArray: [0, 3]
                },

                markers: { size: 0, hover: { size: 5 } },

                // Solo el Equity pinta área; la SMA no (transparente)
                fill: {
                    type: ["gradient", "solid"],
                    opacity: [1, 1],
                    colors: ["#2563eb", "transparent"],
                    gradient: {
                        shadeIntensity: 0.25,
                        opacityFrom: 0.22,
                        opacityTo: 0.02,
                        stops: [0, 70, 100]
                    }
                },

                grid: {
                    borderColor: "rgba(15, 23, 42, 0.08)",
                    strokeDashArray: 4,
                    padding: { left: 8, right: 8 }
                },

                xaxis: {
                    type: "category",
                    categories: d.categories,
                    labels: {
                        style: { colors: "#64748b" },
                        rotate: -35,
                        formatter: (val, idx) => (d.categories.length > 25 && idx % 2 !== 0 ? "" : val)
                    },
                    axisBorder: { color: "rgba(15, 23, 42, 0.08)" },
                    axisTicks: { color: "rgba(15, 23, 42, 0.08)" }
                },

                yaxis: {
                    labels: {
                        style: { colors: "#64748b" },
                        formatter: (v) => this.fmtPercent(v)
                    }
                },

                tooltip: {
                    theme: "light",
                    style: { fontSize: "12px" },
                    x: { formatter: (val) => val }, // ya incluye "#n · fecha"
                    y: { formatter: (v) => this.fmtPercent(v) }
                },

                dataLabels: { enabled: false },
                legend: { show: false }
            };
        },
        equityChartData() {
            const closed = this._getClosedTrades({ unfiltered: false, onlyLastN: false }); // filtered
            if (!closed.length) return { categories: [], equity: [], sma: [] };

            // construir equity desde el más antiguo al más reciente
            let equity = 1;
            const categories = [];
            const equityArr = [];

            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const closeMs = new Date(t.closedAt).getTime();
                if (!Number.isFinite(closeMs)) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const res = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                let r = res.profit / entryValue;
                if (!Number.isFinite(r)) continue;

                r = Math.max(-1, r);
                equity *= (1 + r);

                const idx1 = equityArr.length + 1; // trade # (1..N)
                categories.push(this.fmtTradeXLabel(idx1, closeMs));
                equityArr.push(equity - 1); // 0.10 => 10%
            }

            // SMA(10) sobre la serie de equity (mismo eje X)
            const smaPts = this.calcSMA_XY(equityArr.map((y, i) => ({ x: i, y })), 10);
            const smaArr = smaPts.map(p => p.y);

            return { categories, equity: equityArr, sma: smaArr };
        },
        drawdownChartData() {
            const closed = this._getClosedTrades({ unfiltered: false, onlyLastN: false }); // filtered: más reciente -> más antiguo
            if (!closed.length) return { categories: [], dd: [], sma: [], maxDrawdownAbs: 0 };

            let equity = 1;
            let peak = 1;

            const categories = [];
            const ddArr = [];

            // de más antiguo a más reciente
            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const closeMs = new Date(t.closedAt).getTime();
                if (!Number.isFinite(closeMs)) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const res = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                let r = res.profit / entryValue;
                if (!Number.isFinite(r)) continue;

                r = Math.max(-1, r);
                equity *= (1 + r);

                // actualizar pico
                if (equity > peak) peak = equity;

                // drawdown actual (<=0): equity/peak - 1
                const ddNow = (equity / peak) - 1;

                const idx1 = ddArr.length + 1;
                categories.push(this.fmtTradeXLabel(idx1, closeMs));
                ddArr.push(ddNow);
            }

            // SMA(10) sobre el drawdown
            const smaPts = this.calcSMA_XY(ddArr.map((y, i) => ({ x: i, y })), 10);
            const smaArr = smaPts.map(p => p.y);

            const maxDrawdownAbs = ddArr.length ? Math.abs(Math.min(...ddArr)) : 0;

            return { categories, dd: ddArr, sma: smaArr, maxDrawdownAbs };
        },
        drawdownSeries() {
            const d = this.drawdownChartData;
            return [
                { name: "Drawdown", type: "area", data: d.dd },
                { name: "SMA(10)", type: "line", data: d.sma }
            ];
        },
        drawdownChartOptions() {
            const d = this.drawdownChartData;

            const minY = d.dd.length ? Math.min(...d.dd) : -0.01;
            const yMin = Math.min(minY, -0.01); // al menos -1% para no quedar plano

            return {
                colors: ["#2563eb", "#94a3b8"],

                chart: {
                    type: "area",
                    toolbar: { show: false },
                    foreColor: "#475569",
                    zoom: { enabled: false },
                    background: "transparent",
                    animations: { enabled: true, easing: "easeinout", speed: 650 }
                },

                stroke: {
                    curve: "smooth",
                    width: [3, 2],
                    colors: ["#2563eb", "#94a3b8"],
                    dashArray: [0, 3] // SMA discontinua
                },

                markers: { size: 0, hover: { size: 5 } },

                // área solo para DD (SMA transparente)
                fill: {
                    type: ["gradient", "solid"],
                    opacity: [1, 1],
                    colors: ["#2563eb", "transparent"],
                    gradient: {
                        shadeIntensity: 0.25,
                        opacityFrom: 0.18,
                        opacityTo: 0.02,
                        stops: [0, 70, 100]
                    }
                },

                grid: {
                    borderColor: "rgba(15, 23, 42, 0.08)",
                    strokeDashArray: 4,
                    padding: { left: 8, right: 8 }
                },

                xaxis: {
                    type: "category",
                    categories: d.categories,
                    labels: {
                        style: { colors: "#64748b" },
                        rotate: -35,
                        formatter: (val, idx) => (d.categories.length > 25 && idx % 2 !== 0 ? "" : val)
                    },
                    axisBorder: { color: "rgba(15, 23, 42, 0.08)" },
                    axisTicks: { color: "rgba(15, 23, 42, 0.08)" }
                },

                yaxis: {
                    min: yMin,
                    max: 0,
                    labels: {
                        style: { colors: "#64748b" },
                        formatter: (v) => this.fmtPercent(v)
                    }
                },

                tooltip: {
                    theme: "light",
                    style: { fontSize: "12px" },
                    x: { formatter: (val) => val },
                    y: { formatter: (v) => this.fmtPercent(v) }
                },

                dataLabels: { enabled: false },
                legend: { show: false }
            };
        },
        winnerDurationChartData() {
            const all = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];
            const closed = all.filter(t =>
                t &&
                t.closedAt != null &&
                t.openedAt != null &&
                t.entryAvgPrice != null &&
                t.exitAvgPrice != null &&
                t.qty != null
            );

            if (!closed.length) return { categories: [], dur: [], sma: [], meta: [] };

            // de más antiguo a más reciente
            const categories = [];
            const dur = [];
            const meta = [];

            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const r = this.tradeReturnPctNet(t);
                if (r == null || r <= 0) continue; // winners

                const mins = this.getTradeDurationMinutes(t);
                if (!Number.isFinite(mins) || mins <= 0) continue;

                const closeMs = new Date(t.closedAt).getTime();
                const idx1 = dur.length + 1;

                categories.push(this.fmtTradeXLabel(idx1, closeMs));
                dur.push(mins);
                meta.push({ trade: t, r, mins });
            }

            const smaPts = this.calcSMA_XY(dur.map((y, i) => ({ x: i, y })), 10);
            const sma = smaPts.map(p => p.y);

            return { categories, dur, sma, meta };
        },
        winnerDurationSeries() {
            const d = this.winnerDurationChartData;
            return [
                { name: "Winner duration", type: "area", data: d.dur },
                { name: "SMA(10)", type: "line", data: d.sma }
            ];
        },
        winnerDurationChartOptions() {
            const d = this.winnerDurationChartData;
            const self = this;

            return {
                colors: ["#10b981", "#94a3b8"],

                chart: {
                    type: "area",
                    toolbar: { show: false },
                    foreColor: "#475569",
                    zoom: { enabled: false },
                    background: "transparent",
                    animations: { enabled: true, easing: "easeinout", speed: 650 }
                },

                stroke: {
                    curve: "smooth",
                    width: [3, 2],
                    colors: ["#10b981", "#94a3b8"],
                    dashArray: [0, 6]
                },

                markers: { size: 0, hover: { size: 5 } },

                fill: {
                    type: ["gradient", "solid"],
                    opacity: [1, 1],
                    colors: ["#10b981", "transparent"],
                    gradient: {
                        shadeIntensity: 0.25,
                        opacityFrom: 0.20,
                        opacityTo: 0.02,
                        stops: [0, 70, 100]
                    }
                },

                grid: {
                    borderColor: "rgba(15, 23, 42, 0.08)",
                    strokeDashArray: 4,
                    padding: { left: 8, right: 8 }
                },

                xaxis: {
                    type: "category",
                    categories: d.categories,
                    labels: {
                        style: { colors: "#64748b" },
                        rotate: -35,
                        formatter: (val, idx) => (d.categories.length > 25 && idx % 2 !== 0 ? "" : val)
                    },
                    axisBorder: { color: "rgba(15, 23, 42, 0.08)" },
                    axisTicks: { color: "rgba(15, 23, 42, 0.08)" }
                },

                yaxis: {
                    labels: {
                        style: { colors: "#64748b" },
                        formatter: (v) => self.fmtDurationFromMinutes(v)
                    }
                },

                tooltip: {
                    theme: "light",
                    style: { fontSize: "12px" },
                    custom: ({ series, dataPointIndex }) => {
                        const m = d.meta?.[dataPointIndex];
                        if (!m) return "";

                        const t = m.trade || {};
                        const reason = t.closeReason ? ` · ${t.closeReason}` : "";
                        const pnl = self.fmtSignedPercent(m.r);

                        const durMin = series?.[0]?.[dataPointIndex];
                        const smaMin = series?.[1]?.[dataPointIndex];

                        const durTxt = self.fmtDurationFromMinutes(durMin);
                        const smaTxt = Number.isFinite(smaMin) ? self.fmtDurationFromMinutes(smaMin) : "--";

                        return `
      <div style="padding:8px 10px">
        <div style="font-weight:600">${d.categories[dataPointIndex] || ""}${reason}</div>
        <div>Duration: <b>${durTxt}</b></div>
        <div>P&L: <b>${pnl}</b></div>
        <div>SMA(10): <b>${smaTxt}</b></div>
      </div>
    `;
                    }
                },

                dataLabels: { enabled: false },
                legend: { show: false }
            };
        },
        loserDurationChartData() {
            const all = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];
            const closed = all.filter(t =>
                t &&
                t.closedAt != null &&
                t.openedAt != null &&
                t.entryAvgPrice != null &&
                t.exitAvgPrice != null &&
                t.qty != null
            );

            if (!closed.length) return { categories: [], dur: [], sma: [], meta: [] };

            const categories = [];
            const dur = [];
            const meta = [];

            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const r = this.tradeReturnPctNet(t);
                if (r == null || r >= 0) continue; // losers

                const mins = this.getTradeDurationMinutes(t);
                if (!Number.isFinite(mins) || mins <= 0) continue;

                const closeMs = new Date(t.closedAt).getTime();
                const idx1 = dur.length + 1;

                categories.push(this.fmtTradeXLabel(idx1, closeMs));
                dur.push(mins);
                meta.push({ trade: t, r, mins });
            }

            const smaPts = this.calcSMA_XY(dur.map((y, i) => ({ x: i, y })), 10);
            const sma = smaPts.map(p => p.y);

            return { categories, dur, sma, meta };
        },
        loserDurationSeries() {
            const d = this.loserDurationChartData;
            return [
                { name: "Loser duration", type: "area", data: d.dur },
                { name: "SMA(10)", type: "line", data: d.sma }
            ];
        },
        loserDurationChartOptions() {
            const d = this.loserDurationChartData;
            const self = this;

            return {
                colors: ["#ef4444", "#94a3b8"],

                chart: {
                    type: "area",
                    toolbar: { show: false },
                    foreColor: "#475569",
                    zoom: { enabled: false },
                    background: "transparent",
                    animations: { enabled: true, easing: "easeinout", speed: 650 }
                },

                stroke: {
                    curve: "smooth",
                    width: [3, 2],
                    colors: ["#ef4444", "#94a3b8"],
                    dashArray: [0, 6]
                },

                markers: { size: 0, hover: { size: 5 } },

                fill: {
                    type: ["gradient", "solid"],
                    opacity: [1, 1],
                    colors: ["#ef4444", "transparent"],
                    gradient: {
                        shadeIntensity: 0.25,
                        opacityFrom: 0.20,
                        opacityTo: 0.02,
                        stops: [0, 70, 100]
                    }
                },

                grid: {
                    borderColor: "rgba(15, 23, 42, 0.08)",
                    strokeDashArray: 4,
                    padding: { left: 8, right: 8 }
                },

                xaxis: {
                    type: "category",
                    categories: d.categories,
                    labels: {
                        style: { colors: "#64748b" },
                        rotate: -35,
                        formatter: (val, idx) => (d.categories.length > 25 && idx % 2 !== 0 ? "" : val)
                    },
                    axisBorder: { color: "rgba(15, 23, 42, 0.08)" },
                    axisTicks: { color: "rgba(15, 23, 42, 0.08)" }
                },

                yaxis: {
                    labels: {
                        style: { colors: "#64748b" },
                        formatter: (v) => self.fmtDurationFromMinutes(v)
                    }
                },

                tooltip: {
                    theme: "light",
                    style: { fontSize: "12px" },
                    custom: ({ series, dataPointIndex }) => {
                        const m = d.meta?.[dataPointIndex];
                        if (!m) return "";

                        const t = m.trade || {};
                        const reason = t.closeReason ? ` · ${t.closeReason}` : "";
                        const pnl = self.fmtSignedPercent(m.r);

                        const durMin = series?.[0]?.[dataPointIndex];
                        const smaMin = series?.[1]?.[dataPointIndex];

                        const durTxt = self.fmtDurationFromMinutes(durMin);
                        const smaTxt = Number.isFinite(smaMin) ? self.fmtDurationFromMinutes(smaMin) : "--";

                        return `
      <div style="padding:8px 10px">
        <div style="font-weight:600">${d.categories[dataPointIndex] || ""}${reason}</div>
        <div>Duration: <b>${durTxt}</b></div>
        <div>P&L: <b>${pnl}</b></div>
        <div>SMA(10): <b>${smaTxt}</b></div>
      </div>
    `;
                    }
                },

                dataLabels: { enabled: false },
                legend: { show: false }
            };
        },
        closeReasonsAgg() {
            const list = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];

            const init = (key) => {
                const labelMap = {
                    SL: "Stop Loss",
                    TP: "Take Profit",
                    TIME: "Time Stop",
                    MANUAL: "Manual",
                    ERROR: "Error",
                    UNKNOWN: "Other"
                };

                const colorMap = {
                    SL: "#F87171",     // rojo
                    TP: "#4ADE80",     // verde
                    TIME: "#FBBF24",   // ámbar
                    MANUAL: "#60A5FA", // azul
                    ERROR: "#A78BFA",  // violeta
                    UNKNOWN: "#94A3B8" // gris
                };

                return {
                    key,
                    label: labelMap[key] || key,
                    color: colorMap[key] || "#94A3B8",
                    count: 0,
                    pnlPct: 0 // suma aritmética de returns (decimal)
                };
            };

            const map = new Map();

            for (let i = 0; i < list.length; i++) {
                const t = list[i];
                if (!t || t.closedAt == null) continue;

                const key = t.closeReason || "UNKNOWN";

                const r = this.tradeReturnPctNet(t); // decimal (neto)
                if (r == null || !Number.isFinite(r)) continue;

                if (!map.has(key)) map.set(key, init(key));

                const row = map.get(key);
                row.count += 1;
                row.pnlPct += r;
            }

            // Orden bonito (si existen)
            const order = { SL: 0, TP: 1, TIME: 2, MANUAL: 3, ERROR: 4, UNKNOWN: 5 };
            return Array.from(map.values()).sort(
                (a, b) => (order[a.key] ?? 99) - (order[b.key] ?? 99)
            );
        },
        closeReasonsTotalCount() {
            return this.closeReasonsAgg.reduce((acc, x) => acc + (Number(x.count) || 0), 0);
        },
        closeReasonsSeries() {
            // Donut = nº trades por razón
            return this.closeReasonsAgg.map(r => r.count);
        },
        closeReasonsChartOptions() {
            return {
                chart: { type: "donut", toolbar: { show: false }, foreColor: "#111827" },

                labels: this.closeReasonsAgg.map(r => r.label),
                colors: this.closeReasonsAgg.map(r => r.color),

                legend: { show: false },

                dataLabels: {
                    enabled: true,
                    style: { fontSize: "12px", fontWeight: 500, colors: ["#1e293b"] },
                    dropShadow: { enabled: false }
                },

                plotOptions: {
                    pie: {
                        donut: {
                            size: "72%",
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: "Total",
                                    formatter: () => String(this.closeReasonsTotalCount)
                                }
                            }
                        }
                    }
                },

                tooltip: {
                    y: { formatter: (v) => `${v} trades` }
                }
            };
        },
        exitReasonPnLRows() {
            // Panel derecho: P&L por razón (en %), ordenado por impacto absoluto
            return this.closeReasonsAgg
                .map(r => ({
                    key: r.key,
                    label: r.label,
                    color: r.color,
                    count: r.count,
                    pnlPct: r.pnlPct
                }))
                .sort((a, b) => Math.abs(b.pnlPct) - Math.abs(a.pnlPct));
        },
        byType() {
            const list = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];

            // solo cerrados y con datos mínimos
            const closed = list.filter(t =>
                t &&
                t.closedAt != null &&
                t.entryAvgPrice != null &&
                t.exitAvgPrice != null &&
                t.qty != null
            );

            const longs = closed.filter(t => t.mode === "SPOT_LONG");
            const shorts = closed.filter(t => t.mode !== "SPOT_LONG"); // MARGIN_SHORT

            return {
                long: this.calcAllocationStats(longs, this.tradeFeesPct),
                short: this.calcAllocationStats(shorts, this.tradeFeesPct)
            };
        },
        kpiCards() {
            const pnlStatus = this.metricStatus("kpi.pnlTotal", this.kpis.pnlTotal);
            const pfStatus = this.metricStatus("kpi.profitFactor", this.kpis.profitFactor);
            const rlDDStatus = this.metricStatus("risk.rollingDD", this.statsGlobal.rollingDDAbs);
            const health = this.structuralHealth;

            return [
                { key: "pnlTotal", title: "Total P&L", value: this.fmtPercent(this.calculateTotalPnL(this.tradeFeesPct)), tone: pnlStatus.tone, tooltip: pnlStatus.tooltip, icon: "mdi-trending-up", metricKey: "kpi.pnlTotal" },
                { key: "profitFactor", title: "Profit Factor", value: this.fmtNumber(this.calculateProfitFactor(this.tradeFeesPct)), tone: pfStatus.tone, tooltip: pfStatus.tooltip, icon: "mdi-chart-bar", metricKey: "kpi.profitFactor" },
                { key: "rlDDStatus", title: "Rolling Drawdown", value: this.fmtPercent(this.statsGlobal.rollingDDAbs), tone: rlDDStatus.tone, tooltip: rlDDStatus.tooltip, icon: "mdi-pulse", metricKey: "risk.rollingDD" },
                {
                    key: "systemHealth",
                    title: "System Health",
                    value: `${health.score}/100`,
                    tone: health.tone,
                    tooltip: health.tooltip,
                    icon: "mdi-heart-pulse",
                    metricKey: "system.health" // si quieres, o puedes usar directo
                }
            ];
        },
        miniCards() {
            const wrStatus = this.metricStatus("kpi.winRate", this.kpis.winRate);

            return [
                { key: "tradesTotal", title: "Total Trades", value: String(this.totalTradesClosed), tone: "neutral", icon: "mdi-swap-horizontal" },
                { key: "bestTrade", title: "Best Trade", value: this.fmtPercent(this.bestTradePct), tone: "good", icon: "mdi-trophy" },
                { key: "worstTrade", title: "Worst Trade", value: this.fmtPercent(this.worstTradePct), tone: "bad", icon: "mdi-alert-circle" },
                { key: "winRate", title: "Win Rate", value: this.fmtPercent(this.winRatePct), tone: wrStatus.tone, tooltip: wrStatus.tooltip, icon: "mdi-target", metricKey: "kpi.winRate" },
            ];
        },
        closedTrades() {
            return (this.trades || []).filter(t => String(t.status).toLowerCase() === "closed");
        },
        structuralMetricKeys() {
            return [
                "edge.expectancy.global",
                "kpi.profitFactor",
                "edge.payoffRatio",
                "kpi.maxDrawdown"
            ];
        },
        regimeMetricKeys() {
            return [
                "risk.timeInDD",
                "risk.rollingDD"
            ];
        },
        contextMetricKeys() {
            return [
                "kpi.pnlTotal",
                "kpi.winRate",
                "edge.avgWin",
                "edge.avgLossAbs"
            ];
        },
        monthlyPnlData() {
            const src = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];
            const map = new Map(); // YYYY-MM => trades[]

            for (let i = 0; i < src.length; i++) {
                const t = src[i];
                if (!t || t.closedAt == null) continue;

                const d = new Date(t.closedAt);
                if (Number.isNaN(d.getTime())) continue;

                const y = d.getUTCFullYear();
                const m = String(d.getUTCMonth() + 1).padStart(2, "0");
                const key = `${y}-${m}`;

                if (!map.has(key)) map.set(key, []);
                map.get(key).push(t);
            }

            const rows = Array.from(map.entries())
                .map(([key, trades]) => {
                    // ordenar por cierre dentro del mes para componer correctamente
                    const sorted = trades.slice().sort(
                        (a, b) => new Date(a.closedAt).getTime() - new Date(b.closedAt).getTime()
                    );

                    let equity = 1;
                    let wins = 0;
                    let sumR = 0;
                    let validTrades = 0;

                    for (const t of sorted) {
                        const entry = Number(t.entryAvgPrice);
                        const exit = Number(t.exitAvgPrice);
                        const qty = Number(t.qty);
                        const entryValue = entry * qty;
                        if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                        const res = this.calculateTradeResult(t.mode, entry, exit, qty, this.tradeFeesPct);
                        const r = Number(res.profit) / entryValue;
                        if (!Number.isFinite(r)) continue;

                        validTrades++;
                        sumR += r;
                        if (r > 0) wins++;

                        equity *= (1 + Math.max(-1, r));
                    }

                    return {
                        key,
                        monthlyReturn: validTrades ? (equity - 1) : 0, // <- % mensual real
                        trades: validTrades,
                        winRate: validTrades ? (wins / validTrades) : 0,
                        avgTradePct: validTrades ? (sumR / validTrades) : 0
                    };
                })
                .sort((a, b) => a.key.localeCompare(b.key));

            const categories = rows.map(r => {
                const [y, m] = r.key.split("-");
                return `${m}-${String(y)}`;
            });
            const pnl = rows.map(r => r.monthlyReturn); // lo dejas como pnl para no romper series
            const trades = rows.map(r => r.trades);
            const avgTradePct = rows.map(r => r.avgTradePct);
            const winRate = rows.map(r => r.winRate);
            const sma3 = this.calcSMA_XY(pnl.map((y, x) => ({ x, y })), 3).map(p => p.y);

            return { categories, pnl, trades, avgTradePct, winRate, sma3 };
        },

        monthlyPnlSeries() {
            const d = this.monthlyPnlData;
            return [
                { name: "Monthly Realized Return (%)", type: "column", data: d.pnl }
            ];
        },

        monthlyPnlChartOptions() {
            const d = this.monthlyPnlData;
            return {
                chart: { type: "line", stacked: false, toolbar: { show: false } },
                stroke: { width: [0, 2], curve: "smooth" },
                plotOptions: {
                    bar: {
                        columnWidth: "55%",
                        colors: {
                            ranges: [
                                { from: -Number.MAX_VALUE, to: -0.0000001, color: "#ef4444" },
                                { from: 0, to: Number.MAX_VALUE, color: "#10b981" }
                            ]
                        }
                    }
                },
                xaxis: { categories: d.categories },
                yaxis: [{ labels: { formatter: v => this.fmtPercent(v) } }],
                tooltip: {
                    shared: true,
                    custom: ({ dataPointIndex }) => {
                        const i = dataPointIndex;
                        return `
      <div style="padding:8px 10px">
        <div style="font-weight:600">${d.categories[i] || ""}</div>
        <div>Realized Return: <b>${this.fmtSignedPercent(d.pnl[i])}</b></div>
        <div>Closed Trades: <b>${d.trades[i] ?? 0}</b></div>
        <div>Win Rate: <b>${this.fmtPercent(d.winRate[i] ?? 0)}</b></div>
        <div>Avg Return / Trade: <b>${this.fmtSignedPercent(d.avgTradePct[i] ?? 0)}</b></div>
      </div>
    `;
                    }
                },
                legend: { show: false }
            };
        },
        monthlyClosedTradesData() {
            const src = Array.isArray(this.tradesListFiltered) ? this.tradesListFiltered : [];
            const map = new Map(); // YYYY-MM => count

            for (let i = 0; i < src.length; i++) {
                const t = src[i];
                if (!t || t.closedAt == null) continue;

                const d = new Date(t.closedAt);
                if (Number.isNaN(d.getTime())) continue;

                const y = d.getUTCFullYear();
                const m = String(d.getUTCMonth() + 1).padStart(2, "0");
                const key = `${y}-${m}`;

                map.set(key, (map.get(key) || 0) + 1);
            }

            const rows = Array.from(map.entries())
                .map(([key, count]) => ({ key, count }))
                .sort((a, b) => a.key.localeCompare(b.key));

            const categories = rows.map(r => {
                const [y, m] = r.key.split("-");
                return `${m}-${String(y)}`;
            });
            const counts = rows.map(r => r.count);
            const sma3 = this.calcSMA_XY(counts.map((y, x) => ({ x, y })), 3).map(p => p.y);

            return { categories, counts, sma3 };
        },

        monthlyClosedTradesSeries() {
            const d = this.monthlyClosedTradesData;
            return [
                { name: "Closed Trades", type: "column", data: d.counts }
            ];
        },

        monthlyClosedTradesChartOptions() {
            const d = this.monthlyClosedTradesData;
            return {
                chart: { type: "line", toolbar: { show: false } },
                stroke: { width: [0, 2], curve: "smooth" },
                plotOptions: {
                    bar: { columnWidth: "50%", borderRadius: 4 }
                },
                colors: ["#3b82f6", "#94a3b8"],
                xaxis: { categories: d.categories },
                yaxis: {
                    min: 0,
                    forceNiceScale: true,
                    labels: { formatter: v => String(Math.round(v)) }
                },
                tooltip: {
                    shared: true,
                    custom: ({ dataPointIndex }) => `
    <div style="padding:8px 10px">
      <div style="font-weight:600">${d.categories[dataPointIndex] || ""}</div>
      <div>Closed Trades: <b>${d.counts[dataPointIndex] ?? 0}</b></div>
    </div>
  `
                },
                legend: { show: false }
            };
        }
    },

    mounted() {
        this.getTrades();
    },

    methods: {
        // ---- PETICIONES AL BACKEND ----
        // Consulta el listado de señales activas
        async getTrades() {
            this.loadingTrades = true;
            try {
                const response = await axios.get('/trades/getTrades');
                this.tradesList = response.data;
            } catch (e) {
                this.showErrorModal();
            } finally {
                this.loadingTrades = false;
            }
        },
        // Métodos asociados con utilidades
        closeReasonName(reason) {
            if (reason === "TP") return this.$t('dashboard.table.tp');
            if (reason === "TIME") return this.$t('dashboard.table.timestop');
            return this.$t('dashboard.table.sl');
        },
        formatDate(dateISO) {
            return new Date(dateISO)
                .toLocaleDateString('en-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC'
                })
                .replace('.', '')
                .toLowerCase()
        },
        // Métodos para cálculos matemátricos de trades
        calculateTotalPnL(feePercent = 0) {
            if (!Array.isArray(this.tradesListFiltered)) return 0;

            let equity = 1;

            // recorrer del más antiguo al más reciente (porque tradesListFiltered suele venir reciente->antiguo)
            for (let i = this.tradesListFiltered.length - 1; i >= 0; i--) {
                const trade = this.tradesListFiltered[i];
                if (!trade) continue;

                const { entryAvgPrice, exitAvgPrice, qty, mode, closedAt } = trade;
                if (closedAt == null) continue;
                if (entryAvgPrice == null || exitAvgPrice == null || qty == null) continue;

                const entryPrice = Number(entryAvgPrice);
                const exitPrice = Number(exitAvgPrice);
                const q = Number(qty);

                const entryValue = entryPrice * q;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(mode, entryPrice, exitPrice, q, feePercent);

                // r es retorno (ratio) sobre entryValue
                const r = result.profit / entryValue;
                if (!Number.isFinite(r)) continue;

                // evitar equity negativa si hay un -100%
                const clamped = Math.max(-1, r);
                equity *= (1 + clamped);
            }

            return equity - 1;
        },
        calculateTradeResult(tradeType, entryPrice, exitPrice, quantity, commissionPct) {
            const entryValue = entryPrice * quantity;
            const exitValue = exitPrice * quantity;

            const entryFee = entryValue * (commissionPct / 100);
            const exitFee = exitValue * (commissionPct / 100);
            const totalCommission = entryFee + exitFee;

            let grossProfit;

            if (tradeType === 'SPOT_LONG') {
                grossProfit = exitValue - entryValue;
            } else {
                grossProfit = entryValue - exitValue;
            }

            const netProfit = grossProfit - totalCommission;

            // 🔑 Misma base para long y short
            const profitPercentage = netProfit / entryValue;

            return {
                profit: netProfit,
                profitPercentage
            };
        },
        calculateProfitFactor(feePercent = 0) {
            if (!Array.isArray(this.tradesListFiltered)) return 0;

            let grossProfit = 0; // suma de pnl positivos
            let grossLoss = 0;   // suma de |pnl| negativos

            // recorrer del más antiguo al más reciente (lista viene al revés)
            for (let i = this.tradesListFiltered.length - 1; i >= 0; i--) {
                const trade = this.tradesListFiltered[i];

                const {
                    mode,
                    entryAvgPrice,
                    exitAvgPrice,
                    qty,
                } = trade;

                if (trade.closedAt == null) continue;
                if (qty == null) continue;
                if (entryAvgPrice == null || exitAvgPrice == null) continue;

                const result = this.calculateTradeResult(
                    mode,
                    Number(entryAvgPrice),
                    Number(exitAvgPrice),
                    Number(qty),
                    feePercent
                );

                if (!Number.isFinite(result.profitPercentage) || result.profitPercentage === 0) continue;

                if (result.profitPercentage > 0) grossProfit += result.profitPercentage;
                else grossLoss += Math.abs(result.profitPercentage);
            }

            // Convenciones típicas:
            // - si no hay pérdidas: PF = Infinity (racha perfecta o sin trades perdedores)
            // - si no hay ganancias y sí pérdidas: PF = 0
            if (grossLoss === 0) return grossProfit > 0 ? Infinity : 0;

            return grossProfit / grossLoss;
        },
        calculateMaxDrawdownPct(feePercent = 0) {
            if (!Array.isArray(this.tradesListFiltered) || this.tradesListFiltered.length === 0) {
                return 0;
            }

            let equity = 1;
            let peak = 1;
            let maxDD = 0; // en decimal: 0.25 => 25%

            // recorrer del más antiguo al más reciente
            for (let i = this.tradesListFiltered.length - 1; i >= 0; i--) {
                const trade = this.tradesListFiltered[i];

                const {
                    mode,
                    entryAvgPrice,
                    exitAvgPrice,
                    qty
                } = trade;

                if (entryAvgPrice == null || exitAvgPrice == null || qty == null) continue;

                const result = this.calculateTradeResult(
                    mode,
                    Number(entryAvgPrice),
                    Number(exitAvgPrice),
                    Number(qty),
                    feePercent
                );

                const entryValue = Number(entryAvgPrice) * Number(qty);
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                // retorno neto del trade en base al entryValue
                let r = result.profit / entryValue;

                if (!Number.isFinite(r)) continue;

                // Actualizar equity
                equity *= (1 + r);

                // Si equity cae a 0 o negativo, DD efectivo = 100%
                if (equity <= 0) {
                    maxDD = 1;
                    break;
                }

                // Actualizar peak y DD
                if (equity > peak) peak = equity;

                const ddNow = (peak - equity) / peak;
                if (ddNow > maxDD) maxDD = ddNow;
            }

            return maxDD; // 0.1234 => 12.34%
        },
        calculateExpectancyPerTradePct(onlyLastN = false, unfiltered = false) {
            const closed = this._getClosedTrades({ unfiltered, onlyLastN });
            if (!closed.length) return 0;

            let wins = 0, losses = 0;
            let sumWin = 0, sumLoss = 0;

            for (let i = 0; i < closed.length; i++) {
                const t = closed[i];

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                const r = result.profit / entryValue;
                if (!Number.isFinite(r) || r === 0) continue;

                if (r > 0) { wins++; sumWin += r; }
                else { losses++; sumLoss += r; }
            }

            const total = wins + losses;
            if (total === 0) return 0;

            const avgWin = wins ? (sumWin / wins) : 0;
            const avgLoss = losses ? (sumLoss / losses) : 0;

            const winRate = wins / total;
            const lossRate = losses / total;

            return (winRate * avgWin) + (lossRate * avgLoss);
        },
        calculateAvgWinPct(onlyLastN = false, unfiltered = false) {
            const closed = this._getClosedTrades({ unfiltered, onlyLastN });
            if (!closed.length) return 0;

            let wins = 0;
            let sumWin = 0;

            for (let i = 0; i < closed.length; i++) {
                const t = closed[i];

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                const r = result.profit / entryValue;
                if (!Number.isFinite(r) || r <= 0) continue;

                wins++;
                sumWin += r;
            }

            return wins === 0 ? 0 : (sumWin / wins);
        },
        calculateAvgLossPct(onlyLastN = false, unfiltered = false) {
            const closed = this._getClosedTrades({ unfiltered, onlyLastN });
            if (!closed.length) return 0;

            let losses = 0;
            let sumLoss = 0;

            for (let i = 0; i < closed.length; i++) {
                const t = closed[i];

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                const r = result.profit / entryValue;
                if (!Number.isFinite(r) || r >= 0) continue;

                losses++;
                sumLoss += r;
            }

            return losses === 0 ? 0 : (sumLoss / losses);
        },
        calculatePayoffRatio(onlyLastN = false, unfiltered = false) {
            const avgWin = this.calculateAvgWinPct(onlyLastN, unfiltered);
            const avgLoss = this.calculateAvgLossPct(onlyLastN, unfiltered);

            if (!Number.isFinite(avgWin) || !Number.isFinite(avgLoss)) return 0;
            if (avgWin <= 0) return 0;
            if (avgLoss === 0) return Infinity;

            return avgWin / Math.abs(avgLoss);
        },
        calculateRollingDrawdownPct(onlyLastN = false, unfiltered = false) {
            const closed = this._getClosedTrades({ unfiltered, onlyLastN });
            if (closed.length < 2) return 0;

            let equity = 1;
            let peak = 1;

            // de más antiguo a más reciente
            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                const r = result.profit / entryValue;
                if (!Number.isFinite(r)) continue;

                equity *= (1 + r);
                if (equity <= 0) return -1; // -100%

                if (equity > peak) peak = equity;
            }

            // retorno relativo desde el peak (negativo si hay DD)
            return (equity / peak) - 1;
        },
        calculateTimeInDrawdownDays(onlyLastN = false, unfiltered = false) {
            const closed = this._getClosedTrades({ unfiltered, onlyLastN });
            if (closed.length < 2) return { totalDays: 0, maxStreakDays: 0 };

            const msPerDay = 24 * 60 * 60 * 1000;

            let equity = 1;
            let peak = 1;

            let inDD = false;
            let ddStartMs = null;

            let maxCompletedStreakMs = 0;

            for (let i = closed.length - 1; i >= 0; i--) {
                const t = closed[i];

                const closeMs = new Date(t.closedAt).getTime();
                if (!Number.isFinite(closeMs)) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const result = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
                const r = result.profit / entryValue;
                if (!Number.isFinite(r)) continue;

                equity *= (1 + r);
                if (equity <= 0) {
                    const totalDays = ddStartMs != null ? Math.ceil((Date.now() - ddStartMs) / msPerDay) : 0;
                    const maxStreakDays = Math.ceil(maxCompletedStreakMs / msPerDay);
                    return { totalDays, maxStreakDays };
                }

                if (equity >= peak) {
                    if (inDD && ddStartMs != null) {
                        const completed = Math.max(0, closeMs - ddStartMs);
                        if (completed > maxCompletedStreakMs) maxCompletedStreakMs = completed;
                    }

                    inDD = false;
                    ddStartMs = null;

                    if (equity > peak) peak = equity;
                } else {
                    if (!inDD) {
                        inDD = true;
                        ddStartMs = closeMs;
                    }
                }
            }

            const totalDays = (inDD && ddStartMs != null)
                ? Math.ceil((Date.now() - ddStartMs) / msPerDay)
                : 0;

            const maxStreakDays = maxCompletedStreakMs > 0
                ? Math.ceil(maxCompletedStreakMs / msPerDay)
                : 0;

            return { totalDays, maxStreakDays };
        },
        statusTotalPnl(pnlTotal) {
            const v = Number(pnlTotal) || 0;

            // Total PnL es contextual: no quiero rojo por un -1% o -2%
            if (v >= 0) return { tone: "good", tooltip: "Total P&L ≥ 0 (contextual KPI). Good sign." };
            if (v >= -0.03) return { tone: "warn", tooltip: "Total P&L between 0% and -3%: drawdown zone (contextual)." };
            return { tone: "bad", tooltip: "Total P&L < -3%: contextual red." };
        },
        statusWinRate(winRate, payoffRatio = null) {
            const w = Number(winRate) || 0;
            const p = Number(payoffRatio);

            // WinRate “correcto” depende del payoff:
            // break-even win rate ≈ 1 / (1 + payoff)
            const be = (Number.isFinite(p) && p > 0) ? (1 / (1 + p)) : 0.45;

            const goodMin = be + 0.07; // margen saludable sobre BE
            const warnMin = be + 0.03;

            if (w >= Math.max(0.35, goodMin)) {
                return { tone: "good", tooltip: `Win rate healthy vs payoff (BE≈${Math.round(be * 100)}%).` };
            }
            if (w >= Math.max(0.25, warnMin)) {
                return { tone: "warn", tooltip: `Win rate near break-even vs payoff (BE≈${Math.round(be * 100)}%).` };
            }
            return { tone: "bad", tooltip: `Win rate below break-even vs payoff (BE≈${Math.round(be * 100)}%).` };
        },
        statusProfitFactor(pf) {
            let v = Number(pf);
            if (!Number.isFinite(v)) v = Infinity;

            // PF global: verde si ya es claramente > 1.4
            if (v >= 1.5) return { tone: "good", tooltip: "Profit Factor ≥ 1.5 (healthy)." };
            if (v >= 1.2) return { tone: "warn", tooltip: "Profit Factor 1.2–1.5 (watch)." };
            return { tone: "bad", tooltip: "Profit Factor < 1.2 (possible structural issue if persistent)." };
        },
        statusMaxDrawdown(maxDrawdownAbs) {
            const ddAbs = Math.abs(Number(maxDrawdownAbs) || 0);
            const base = Math.abs(Number(this.baselines.expectedMaxDrawdownAbs));

            // Un poco más estricto: verde hasta 0.75×, amarillo hasta 1.5×, rojo > 1.5×
            const greenMax = 0.75 * base;
            const redMin = 1.5 * base;

            if (ddAbs <= greenMax) return { tone: "good", tooltip: `Max DD within expected (≤ ${this.fmtPercent(-greenMax)})` };
            if (ddAbs <= redMin) return { tone: "warn", tooltip: `Max DD elevated (≤ ${this.fmtPercent(-redMin)})` };
            return { tone: "bad", tooltip: `Max DD > 1.5× expected (>${this.fmtPercent(-redMin)})` };
        },
        statusExpectancyGlobal(exp) {
            const v = Number(exp) || 0;

            // Verde más exigente: 0.15% por trade (0.0015)
            if (v >= 0.0015) return { tone: "good", tooltip: "Expectancy ≥ +0.15% per trade (edge is strong)." };
            if (v >= 0) return { tone: "warn", tooltip: "Expectancy between 0% and +0.15% (watch, no action by itself)." };
            return { tone: "bad", tooltip: "Negative expectancy (global) → potential edge degradation." };
        },
        statusExpectancyRolling(exp, consecutiveNegCount) {
            const v = Number(exp) || 0;
            const streak = Number(consecutiveNegCount) || 0;

            // Rojo solo si persistente (y realmente negativo)
            if (v < 0 && streak >= 10) {
                return { tone: "bad", tooltip: "Rolling expectancy < 0 for ≥ 10 windows (persistent red)." };
            }

            // Verde si ya es “claramente” positivo
            if (v >= 0.0005) return { tone: "good", tooltip: "Rolling expectancy ≥ +0.05% per trade (edge currently present)." };

            // Cerca de 0 => amarilla (micro-regime)
            if (this.isNearZero(v, 0.0005)) return { tone: "warn", tooltip: "Rolling expectancy ~ 0 (flat / choppy regime)." };

            // Negativa pero aún no persistente => amarilla
            return { tone: "warn", tooltip: "Rolling expectancy < 0, not persistent yet (observe)." };
        },
        statusAvgWin(avgWin) {
            const v = Number(avgWin) || 0;

            const goodMin = 0.03;  // 3%
            const warnMin = 0.02; // 2%

            if (v >= goodMin) {
                return { tone: "good", tooltip: "Avg Win ≥ 3% (healthy winners)." };
            }
            if (v >= warnMin) {
                return { tone: "warn", tooltip: "Avg Win 2%–3% (winners smaller than desired)." };
            }
            return { tone: "bad", tooltip: "Avg Win < 2% (winners too small; edge may be eroding)." };
        },
        statusAvgLossAbs(avgLossAbs) {
            const v = Number(avgLossAbs) || 0; // ya es abs

            const goodMax = 0.0225; // 2.25%
            const warnMax = 0.0275; // 2.75%

            if (v <= goodMax) {
                return { tone: "good", tooltip: "Avg Loss ≤ 2.25% (losses controlled)." };
            }
            if (v <= warnMax) {
                return { tone: "warn", tooltip: "Avg Loss 2.25%–2.75% (losses creeping up)." };
            }
            return { tone: "bad", tooltip: "Avg Loss > 2.75% (losses too large)." };
        },
        statusPayoffRatio(payoff) {
            let v = Number(payoff);
            if (!Number.isFinite(v)) v = Infinity;

            if (v >= 1.8) return { tone: "good", tooltip: "Payoff Ratio ≥ 1.8 (strong payoff)." };
            if (v >= 1.3) return { tone: "warn", tooltip: "Payoff Ratio 1.3–1.8 (watch)." };
            return { tone: "bad", tooltip: "Payoff Ratio < 1.3 (edge can break fast)." };
        },
        statusTimeInDrawdown(daysInDD) {
            const v = Number(daysInDD) || 0;
            const base = Number(this.baselines.timeInDDDaysHist);

            const warnMax = Math.max(base + 2, Math.ceil(2 * base));

            if (v <= base) return { tone: "good", tooltip: "Current time in drawdown historical norms." };
            if (v <= warnMax) return { tone: "warn", tooltip: "Current drawdown time elevated (watch)." };
            return { tone: "bad", tooltip: "Long current drawdown (context/risk warning)." };
        },
        statusRollingDrawdown(maxDrawdownAbsRolling) {
            const ddAbs = Math.abs(Number(maxDrawdownAbsRolling));
            const base = Math.abs(Number(this.baselines.rollingDrawdownAbsMean));

            // Más tolerante (para no disparar rojos por micro ruido):
            if (ddAbs <= 0.75 * base) return { tone: "good", tooltip: "Rolling DD at/below mean." };
            if (ddAbs <= 1.5 * base) return { tone: "warn", tooltip: "Rolling DD elevated (microstructure getting choppy)." };
            return { tone: "bad", tooltip: "Rolling DD very high vs mean (regime warning)." };
        },
        isNearZero(v, eps = 0.0003) {
            return Math.abs(Number(v) || 0) <= eps;
        },
        metricStatus(key, value, extra = {}) {
            let res = { tone: "neutral", tooltip: "" };

            switch (key) {
                case "kpi.pnlTotal": res = this.statusTotalPnl(value); break;

                // 👇 aquí le pasamos payoff
                case "kpi.winRate": res = this.statusWinRate(value, extra.payoffRatio ?? this.statsGlobal.payoffRatio); break;

                case "kpi.maxDrawdown": res = this.statusMaxDrawdown(value); break;

                case "edge.expectancy.global": res = this.statusExpectancyGlobal(value); break;
                case "edge.expectancy.rolling": res = this.statusExpectancyRolling(value, extra.consecutiveNeg || 0); break;

                case "edge.avgWin": res = this.statusAvgWin(value); break;
                case "edge.avgLossAbs": res = this.statusAvgLossAbs(value); break;
                case "edge.payoffRatio": res = this.statusPayoffRatio(value); break;
                case "kpi.profitFactor": res = this.statusProfitFactor(value); break;

                case "risk.timeInDD": res = this.statusTimeInDrawdown(value); break;
                case "risk.rollingDD": res = this.statusRollingDrawdown(value); break;

                // alloc...
                case "alloc.long.winRate":
                case "alloc.short.winRate":
                    res = this.statusWinRate(value, extra.payoffRatio ?? this.statsGlobal.payoffRatio);
                    break;

                case "alloc.long.profitFactor":
                case "alloc.short.profitFactor":
                    res = this.statusProfitFactor(value);
                    break;

                case "alloc.long.pnlTotal":
                case "alloc.short.pnlTotal":
                    res = this.statusTotalPnl(value);
                    break;

                case "alloc.long.avgTradePct":
                case "alloc.short.avgTradePct":
                    res = this.statusAvgTradePct(value);
                    break;
            }

            return {
                ...res,
                icon: this.statusIcon(res.tone),
                class: this.statusClass(res.tone)
            };
        },
        statusAvgTradePct(avgTradePct) {
            const v = Number(avgTradePct) || 0;
            if (v > 0) return { tone: "good", tooltip: "Average trade return is positive for this side" };
            if (this.isNearZero(v)) return { tone: "warn", tooltip: "Average trade return is near 0 (flat performance)" };
            return { tone: "bad", tooltip: "Average trade return is negative for this side" };
        },
        getTradeDurationMinutes(trade) {
            const entry = this.parseAnyTs(trade.openedAt ?? trade.entryTs);
            const exit = this.parseAnyTs(trade.closedAt ?? trade.exitTs);

            if (!entry || !exit || exit <= entry) return 0;
            return (exit - entry) / 60000; // minutes (float)
        },
        calcAllocationStats(trades, feePercent = 0) {
            const arr = Array.isArray(trades) ? trades : [];
            if (arr.length === 0) {
                return { trades: 0, winRate: 0, avgTradePct: 0, profitFactor: 0, pnlPct: 0 };
            }

            let count = 0;
            let wins = 0;

            let sumR = 0;        // media aritmética de returns (decimal)
            let equity = 1;      // para Total P&L% compuesto
            let grossProfit = 0; // suma pnl neto positivo (quote)
            let grossLoss = 0;   // suma abs pnl neto negativo (quote)

            // IMPORTANTE: los trades vienen más reciente -> más antiguo
            // Para compounding, recorremos al revés: antiguo -> reciente
            for (let i = arr.length - 1; i >= 0; i--) {
                const t = arr[i];
                if (!t) continue;

                const entryPrice = Number(t.entryAvgPrice);
                const exitPrice = Number(t.exitAvgPrice);
                const qty = Number(t.qty);

                const entryValue = entryPrice * qty;
                if (!Number.isFinite(entryValue) || entryValue <= 0) continue;

                const res = this.calculateTradeResult(t.mode, entryPrice, exitPrice, qty, feePercent);

                // pnl neto (comisiones incluidas)
                let netProfit = Number(res.profit);
                if (!Number.isFinite(netProfit)) continue;

                // Si quieres incluir aquí tu coste fijo máximo de margin (en quote), descomenta:
                // if (t.mode === "MARGIN_SHORT") netProfit -= (Number(this.maxMarginCostQuote) || 0);

                const r = netProfit / entryValue; // return neto decimal
                if (!Number.isFinite(r)) continue;

                count++;
                sumR += r;

                if (r > 0) wins++;

                // Para no reventar equity si hay casos extremos
                const rClamped = Math.max(-1, r);
                equity *= (1 + rClamped);

                if (netProfit > 0) grossProfit += netProfit;
                else if (netProfit < 0) grossLoss += Math.abs(netProfit);
            }

            const tradesCount = count;
            const winRate = tradesCount ? (wins / tradesCount) : 0;
            const avgTradePct = tradesCount ? (sumR / tradesCount) : 0;

            // Total P&L% compuesto (como tu calculateTotalPnL)
            const pnlPct = tradesCount ? (equity - 1) : 0;

            // Profit Factor (finito para que fmtNumber no pinte "-")
            const profitFactor =
                grossLoss > 0 ? (grossProfit / grossLoss) : (grossProfit > 0 ? 999 : 0);

            return {
                trades: tradesCount,
                winRate,
                avgTradePct,
                profitFactor,
                pnlPct
            };
        },

        // Métodos asociados con estilos
        closeReasonColor(reason) {
            if (reason === "TP") return "green";
            if (reason === "TIME") return "amber";
            return "red";
        },
        toneClass(tone) {
            if (tone === "good") return "td-good";
            if (tone === "bad") return "td-bad";
            return "td-neutral";
        },
        iconColor(tone) {
            if (tone === "good") return "green darken-1";
            if (tone === "bad") return "red darken-1";
            return "blue darken-1";
        },
        statusIcon(tone) {
            if (tone === "good") return "mdi-check-circle";
            if (tone === "warn") return "mdi-alert";
            if (tone === "bad") return "mdi-close-circle";
            return "mdi-minus-circle";
        },
        statusClass(tone) {
            if (tone === "good") return "td-good";
            if (tone === "warn") return "td-warn";
            if (tone === "bad") return "td-bad";
            return "td-neutral";
        },

        // Helpers
        _getTradeMs(t) {
            if (!t) return null;
            const dt = t.closedAt != null ? new Date(t.closedAt)
                : t.openedAt != null ? new Date(t.openedAt)
                    : null;

            const ms = dt ? dt.getTime() : NaN;
            return Number.isFinite(ms) ? ms : null;
        },

        _getClosedTrades({ unfiltered = false, onlyLastN = false } = {}) {
            const src = unfiltered ? this.tradesList : this.tradesListFiltered;

            let closed = (Array.isArray(src) ? src : []).filter(t =>
                t &&
                t.closedAt != null &&
                t.entryAvgPrice != null &&
                t.exitAvgPrice != null &&
                t.qty != null
            );

            // Asegurar orden: más reciente -> más antiguo
            closed = closed.slice().sort((a, b) => (this._getTradeMs(b) || 0) - (this._getTradeMs(a) || 0));

            if (onlyLastN) {
                const n = Number(this.rollingWindow) || 20;
                closed = closed.slice(0, n); // si hay menos de N, devuelve los que haya
            }

            return closed;
        },
        calcSMA_XY(points, window = 10) {
            const w = Math.max(1, Number(window) || 10);
            const out = [];
            let sum = 0;
            const q = [];

            for (let i = 0; i < points.length; i++) {
                const y = Number(points[i]?.y);
                if (!Number.isFinite(y)) continue;

                q.push(y);
                sum += y;

                if (q.length > w) sum -= q.shift();

                out.push({
                    x: points[i].x,
                    y: sum / q.length
                });
            }

            return out;
        },
        fmtTradeXLabel(tradeIndex1, isoOrMs) {
            const ms = typeof isoOrMs === "number" ? isoOrMs : Date.parse(isoOrMs);
            if (!Number.isFinite(ms)) return `#${tradeIndex1}`;

            // Ej: "05 feb" (sin puntos)
            const d = new Date(ms).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
            return `#${tradeIndex1} · ${d.replace(".", "").toLowerCase()}`;
        },
        tradeReturnPctNet(trade) {
            if (!trade || trade.closedAt == null) return null;

            const entryPrice = Number(trade.entryAvgPrice);
            const exitPrice = Number(trade.exitAvgPrice);
            const qty = Number(trade.qty);

            const entryValue = entryPrice * qty;
            if (!Number.isFinite(entryValue) || entryValue <= 0) return null;

            const res = this.calculateTradeResult(trade.mode, entryPrice, exitPrice, qty, this.tradeFeesPct);
            let r = res.profit / entryValue;

            if (!Number.isFinite(r)) return null;

            // evita casos extremos (equity <= 0). Para durations no es crítico, pero lo dejamos consistente.
            r = Math.max(-1, r);
            return r; // decimal: 0.02 = +2%
        },
        fmtNumber(v, d = 2) {
            const n = Number(v);
            if (!isFinite(n)) return "-";
            return n.toFixed(d);
        },
        fmtMoney(v) {
            const n = Number(v);
            if (!isFinite(n)) return "-";
            const abs = Math.abs(n);
            const txt = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (n < 0 ? "-$" : "$") + txt;
        },
        fmtSignedMoney(v) {
            const n = Number(v);
            if (!isFinite(n)) return "-";
            const sign = n > 0 ? "+" : "";
            return sign + this.fmtMoney(n);
        },
        fmtPercent(v) {
            const n = Number(v);
            if (!isFinite(n)) return "-";
            return (n * 100).toFixed(2) + "%";
        },
        fmtSignedPercent(v) {
            const n = Number(v);
            if (!isFinite(n)) return "-";
            const sign = n > 0 ? "+" : "";
            return sign + this.fmtPercent(n);
        },
        parseAnyTs(v) {
            if (!v) return null;
            if (typeof v === "number") return v;     // epoch ms
            const t = Date.parse(v);                  // ISO string
            return Number.isNaN(t) ? null : t;
        },
        fmtDurationFromMinutes(min) {
            const m = Math.max(0, Math.round(Number(min) || 0));
            if (!m) return "0m";
            const d = Math.floor(m / (60 * 24));
            const h = Math.floor((m % (60 * 24)) / 60);
            const mm = m % 60;
            if (d > 0) return `${d}d ${h}h`;
            if (h > 0) return `${h}h ${mm}m`;
            return `${mm}m`;
        },

        calcSMA(points, window = 10) {
            const w = Math.max(1, Number(window) || 10);
            const out = [];
            let sum = 0;
            const q = []; // cola de valores

            for (let i = 0; i < points.length; i++) {
                const y = Number(points[i].y) || 0;
                q.push(y);
                sum += y;

                if (q.length > w) sum -= q.shift();

                const avg = sum / q.length;
                out.push({ x: points[i].x, y: Number(avg.toFixed(2)) });
            }
            return out;
        }
    }
};
</script>

<style scoped>
/* ====== PREMIUM LIGHT DASHBOARD (balanced + minimal) ====== */
:global(:root) {
    --bg: #f7f8fb;
    --card: rgba(255, 255, 255, 0.88);
    --card-solid: #ffffff;

    --border: rgba(17, 24, 39, 0.08);
    --border-strong: rgba(17, 24, 39, 0.12);

    --text: #0f172a;
    --title: #334155;
    --body: #1e293b;
    --muted: #64748b;
    --muted2: #94a3b8;

    --accent: #3b82f6;
    --accent-soft: rgba(59, 130, 246, 0.08);

    --good: #16a34a;
    --good-soft: rgba(34, 197, 94, 0.10);

    --bad: #dc2626;
    --bad-soft: rgba(239, 68, 68, 0.10);

    --warn: #ca8a04;
    --warn-soft: rgba(202, 138, 4, 0.10);

    --shadow: 0 10px 30px rgba(2, 6, 23, 0.08);
    --shadow-soft: 0 6px 18px rgba(2, 6, 23, 0.06);
    --radius: 14px;
}


/* Root background */
.td-root {
    min-height: 100vh;
    background:
        radial-gradient(900px 500px at 18% 0%, rgba(59, 130, 246, 0.06), transparent 55%),
        radial-gradient(700px 400px at 92% 15%, rgba(34, 197, 94, 0.035), transparent 55%),
        var(--bg);
    padding-top: 14px;
}

/* Card base */
.td-surface {
    background: var(--card-solid) !important;
    border: 1px solid var(--border) !important;
    border-radius: var(--radius) !important;
    box-shadow: var(--shadow-soft) !important;
    overflow: hidden;
}

/* Slight “glass” header feel (optional but premium) */
.td-surface.td-header {
    background: var(--card) !important;
    backdrop-filter: blur(10px);
}

/* Soft hover lift */
.td-surface:hover {
    box-shadow: var(--shadow) !important;
    transform: translateY(-1px);
    transition: box-shadow 180ms ease, transform 180ms ease;
}

/* Avoid janky hover on big table wrapper */
.td-table .td-surface:hover,
.td-table:hover {
    transform: none;
}

/* Typography */
.td-heading {
    color: var(--title) !important;
    letter-spacing: -0.02em;
}

.td-muted {
    color: var(--muted) !important;
}

.td-title {
    padding: 18px 18px 14px 18px !important;
    align-items: center;
}

/* Section + card titles (less “strong”) */
.td-card-title,
.td-section-title {
    font-weight: 500 !important;
    color: var(--title) !important;
    letter-spacing: -0.01em;
}

/* KPI values */
.td-kpi-value {
    font-weight: 600 !important;
    color: var(--body) !important;
}

.td-kpi-sub {
    color: var(--muted2) !important;
    font-size: 0.75rem !important;
}

/* Inputs sizing */
.td-range {
    max-width: 230px;
}

.td-search {
    max-width: 280px;
}

/* Icon container: keep it subtle */
.td-icon-bg {
    background: var(--accent-soft) !important;
    border: none !important;
}

/* status text colors */
.td-good {
    color: var(--good) !important;
}

.td-bad {
    color: var(--bad) !important;
}

.td-warn {
    color: var(--warn) !important;
}

.td-neutral {
    color: var(--body) !important;
}

/* KPI card padding refinement */
.td-surface .v-card__text {
    padding: 18px !important;
}

/* Dot legend */
.td-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    display: inline-block;
    margin-right: 10px;
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.03);
}

/* Subcards (long/short): subtle rails */
.td-subcard {
    background: var(--card-solid) !important;
    border: 1px solid var(--border) !important;
    border-radius: 12px !important;
    box-shadow: none !important;
}

.td-subcard-good {
    border-left: 4px solid rgba(22, 163, 74, 0.40) !important;
}

.td-subcard-bad {
    border-left: 4px solid rgba(220, 38, 38, 0.40) !important;
}

/* Key-values */
.td-kv {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 7px 0;
    color: var(--muted) !important;
}

.td-kv b {
    color: var(--body) !important;
    font-weight: 600;
}

/* Table: enterprise clean (less contrast) */
.td-table {
    border-top: 1px solid rgba(17, 24, 39, 0.06);
}

/* Header */
.td-table ::v-deep .v-data-table-header th {
    color: var(--muted) !important;
    background: linear-gradient(#fbfcff, #f8fafc) !important;
    border-bottom: 1px solid rgba(17, 24, 39, 0.08) !important;
    font-weight: 600 !important;
    letter-spacing: 0.01em;
}

/* Rows */
.td-table ::v-deep tbody td {
    border-bottom: 1px solid rgba(17, 24, 39, 0.06) !important;
    color: var(--title) !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    font-size: 0.875rem !important;
}

.td-table ::v-deep tbody tr:hover {
    background: rgba(15, 23, 42, 0.025) !important;
}

/* Chips: minimal (outline subtle) */
.td-table ::v-deep .v-chip {
    border-radius: 999px !important;
    font-weight: 500 !important;
    letter-spacing: 0.01em;
    background: transparent !important;
}

/* If you set chip color via Vuetify (success/error/warning), these help reduce intensity */
.td-table ::v-deep .v-chip.success {
    color: var(--good) !important;
    border: 1px solid rgba(22, 163, 74, 0.25) !important;
}

.td-table ::v-deep .v-chip.error {
    color: var(--bad) !important;
    border: 1px solid rgba(220, 38, 38, 0.25) !important;
}

.td-table ::v-deep .v-chip.warning {
    color: var(--warn) !important;
    border: 1px solid rgba(202, 138, 4, 0.25) !important;
}

/* Apex charts: softer text on light bg */
.td-root ::v-deep .apexcharts-text,
.td-root ::v-deep .apexcharts-legend-text {
    fill: var(--muted) !important;
}

/* Make apex tooltips less “heavy” if you use them */
.td-root ::v-deep .apexcharts-tooltip {
    border: 1px solid rgba(17, 24, 39, 0.10) !important;
    box-shadow: 0 8px 24px rgba(2, 6, 23, 0.10) !important;
}

.td-exit-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 6px 4px;
}

.td-panel-title {
    font-weight: 600;
    color: #334155;
    /* slate-700 */
    letter-spacing: -0.01em;
    margin-bottom: 2px;
}

.td-exit-row {
    padding: 10px 10px;
    border: 1px solid rgba(17, 24, 39, 0.06);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.65);
}

.td-exit-row-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.td-exit-label {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #334155;
    font-weight: 500;
}

.td-exit-value {
    font-weight: 600;
}

.td-exit-row-sub {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 4px;
    font-size: 12px;
}

.td-mini-bar {
    height: 6px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.06);
    overflow: hidden;
    margin-top: 8px;
}

.td-mini-bar-fill {
    height: 100%;
    border-radius: 999px;
    opacity: 0.55;
    /* suaviza el color */
}

.td-exit-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 8px 10px 0;
}

.td-block-title {
    font-weight: 600;
    margin: 2px 0 10px;
}

.td-metric-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

@media (max-width: 960px) {
    .td-metric-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .td-metric-grid {
        grid-template-columns: 1fr;
    }
}

.td-metric {
    border: 1px solid rgba(17, 24, 39, 0.06);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.65);
    padding: 12px 12px;
}

.td-metric-label {
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.td-metric-value {
    margin-top: 6px;
    font-size: 1.1rem;
    font-weight: 650;
    color: var(--body);
}

.td-metric-sub {
    margin-top: 2px;
    font-size: 0.75rem;
}

.tooltip-text {
    white-space: pre-line;
}
</style>